// Per-section help — the "?" button in the top-right of every ORDER band on
// the SyQi home dashboard. Explains how to fill that section out (the section
// itself, then a line per tool the user actually has enabled), and lets them
// ask SyQi a follow-up about it without leaving the dashboard.
//
// The Q&A runs through the normal SyQi chat endpoint on a throwaway thread:
// useSyqiChat with enabled:false (no last-conversation resume) and
// persistLastConversation:false, so asking for help never hijacks the user's
// real SyQi conversation.
import React, { useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Send } from 'lucide-react-native';
import { Gear, TOOL_NAMES } from '../domain/olmps';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { ORDER_SECTION_META } from './orderSections';
import { engagementModeFor } from './toolEngagement';
import { useSyqiChat } from './useSyqiChat';
import { GlassFill } from './glass';

/** How to fill out each band: what the section is for, then the moves. */
const SECTION_HOWTO: Record<Gear, { lead: string; steps: string[] }> = {
  orient: {
    lead: 'Point the day before you spend it.',
    steps: [
      'Read the compass first — a sector off trajectory is the day’s real headline.',
      'Answer each tool’s question honestly; a one-line answer beats a skipped box.',
      'Leave this section knowing which direction today serves.',
    ],
  },
  regulate: {
    lead: 'Name what the day will cost you.',
    steps: [
      'Check what is already committed before adding anything new.',
      'Log the state of your resources — time, energy, body, money, people.',
      'If something is short, say so here rather than discovering it at 4pm.',
    ],
  },
  direct: {
    lead: 'Turn the direction into a route.',
    steps: [
      'Pick the projects that actually move today; the rest wait.',
      'Put the work on the calendar — an unscheduled step is a wish.',
      'Say which project stage you are moving, not just which project.',
    ],
  },
  execute: {
    lead: 'Do it, and mark it done.',
    steps: [
      'Work the list top-down; tick things off as you finish them.',
      'Decisions blocking the work belong here — name the call and your leaning.',
      'A dot only goes green when something real got closed.',
    ],
  },
  revise: {
    lead: 'Close the loop while it is still warm.',
    steps: [
      'Write what actually happened, not what you planned.',
      'Flag what slipped and whether it needs rescheduling or dropping.',
      'Anything the plan does not know yet goes back into ORIENT tomorrow.',
    ],
  },
};

/** Per tool: the exact move that turns its dot green, and why it is asked for.
 *  `green` is only set for tools whose own widget records the engagement
 *  (engagementModeFor === 'native'); every other tool goes green by answering
 *  SyQi's question in the box under it, so it shares CLARIFY_GREEN below. */
const CLARIFY_GREEN = 'Answer SyQi’s question in the box under the widget and hit send. One honest line counts.';

const TOOL_HOWTO: Record<string, { green?: string; why: string }> = {
  telos: { why: 'Sectors drift quietly. Saying out loud whether you re-aim or push through is what stops a slow drift becoming a lost quarter.' },
  astrs: { why: 'The compass shows every sector, project and task in one orbit — naming what you are watching is what makes you look at it.' },
  clio: {
    green: 'Type an entry and hit send.',
    why: 'The morning entry is the day’s intent on record — REVISE compares against it tonight.',
  },
  sofia: { why: 'The thing you are least sure about runs in the background all day. Named, it stops costing you attention.' },
  numa: {
    green: 'Resolve at least one commitment — done, rescheduled, or dropped.',
    why: 'Untouched commitments carry over silently and quietly wreck the next day’s capacity.',
  },
  moyri: {
    green: 'Place your lines — tap the circles to set how much of each resource today gets.',
    why: 'Deciding the spend up front is what keeps one sector from eating all of it.',
  },
  argos: { why: 'Flow score without a cause is a number. Naming the drag is what makes it fixable.' },
  akeso: { why: 'The body sets the ceiling for everything else in the day. Log it before it forces the issue.' },
  hrmez: { why: 'Unsent messages block other people’s work, not just yours.' },
  kyrs: {
    green: 'Load or complete a task in the list.',
    why: 'A day with nothing loaded gets spent by whoever asks first.',
  },
  krnos: { why: 'The calendar is the honest version of your plan. Moving what will not survive contact beats discovering it at 4pm.' },
  tkne: { why: 'Project progress happens at the step level. Naming the stage you are moving stops "worked on it" from counting as progress.' },
  metis: {
    green: 'Complete one of the small items.',
    why: 'The minute-sized things are the ones blocking the hour-sized ones.',
  },
  themis: {
    green: 'Tick a commitment’s checkbox as you finish it.',
    why: 'Marking it done is what closes the loop the rest of ORDER scores against.',
  },
  jnus: { why: 'An unmade decision blocks work silently. Writing your leaning usually makes the call obvious.' },
  epic: { why: 'A one-line headline forces the day into something you can actually judge.' },
  oracle: { why: 'The plan cannot adjust to what it has not been told changed.' },
  nemos: { why: 'Overdue items either get a new date or get dropped on purpose. Leaving them is the one option that costs you.' },
};

export function SectionHelpPopup({
  gear,
  tools,
  onClose,
}: {
  /** Open when a gear is set; the caller renders one popup for all sections. */
  gear: Gear | null;
  /** The tools the user has enabled in this section, in section order. */
  tools: string[];
  onClose: () => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const [question, setQuestion] = useState('');
  const chat = useSyqiChat({ enabled: false, persistLastConversation: false });
  const answer = [...chat.messages].reverse().find((m) => m.role === 'assistant');

  if (!gear) return null;
  const meta = ORDER_SECTION_META[gear];
  const howto = SECTION_HOWTO[gear];

  const close = () => {
    setQuestion('');
    onClose();
  };

  const ask = () => {
    const q = question.trim();
    if (!q || chat.sending) return;
    const toolList = tools.map((id) => TOOL_NAMES[id] ?? id.toUpperCase()).join(', ') || 'no tools yet';
    void chat.send(
      `I'm on the ${meta.title.toUpperCase()} section of my SyQi ORDER dashboard — the one that answers "${meta.question}". ` +
        `The tools I have enabled in it are: ${toolList}. ` +
        `Answer only about how to fill out or use this section. My question: ${q}`
    );
    setQuestion('');
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <Pressable style={styles.backdrop} onPress={close}>
        <Pressable style={styles.card} onPress={() => {}}>
          <GlassFill strong radius={radii.panel} />
          <Pressable onPress={close} hitSlop={10} style={styles.close} accessibilityLabel="Close">
            <Text style={styles.closeText}>✕</Text>
          </Pressable>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            <View style={styles.titleRow}>
              <View style={[styles.titleDot, { backgroundColor: meta.color }]} />
              <Text style={styles.title}>{meta.title.toUpperCase()}</Text>
            </View>
            <Text style={styles.question}>{meta.question}</Text>
            <Text style={styles.lead}>{howto.lead}</Text>
            {howto.steps.map((step) => (
              <View key={step} style={styles.bulletRow}>
                <Text style={[styles.bulletDot, { color: t.goldBright }]}>•</Text>
                <Text style={styles.bulletText}>{step}</Text>
              </View>
            ))}

            {tools.length > 0 && (
              <>
                <Text style={[styles.subhead, { color: t.goldBright }]}>Turning the dots green</Text>
                <Text style={styles.bulletText}>
                  One dot per tool in this band. Red means you have not engaged with it today, green means you have.
                  The day is done when every dot in every band is green.
                </Text>
                {tools.map((toolId) => {
                  const howtoTool = TOOL_HOWTO[toolId];
                  const green =
                    howtoTool?.green ??
                    (engagementModeFor(toolId) === 'clarify'
                      ? CLARIFY_GREEN
                      : 'Use the widget below — completing something in it records the engagement.');
                  return (
                    <View key={toolId} style={styles.toolRow}>
                      <Text style={[styles.toolName, { color: t.goldBright }]}>
                        {TOOL_NAMES[toolId] ?? toolId.toUpperCase()}
                      </Text>
                      <Text style={styles.toolText}>
                        <Text style={styles.greenLabel}>Green when: </Text>
                        {green}
                      </Text>
                      {howtoTool && (
                        <Text style={styles.toolText}>
                          <Text style={[styles.whyLabel, { color: t.goldBright }]}>Why: </Text>
                          {howtoTool.why}
                        </Text>
                      )}
                    </View>
                  );
                })}
              </>
            )}

            {answer && <Text style={[styles.answer, answer.isError && styles.answerError]}>{answer.content}</Text>}
          </ScrollView>

          <View style={styles.askRow}>
            <TextInput
              style={[styles.input, webNoFocusRing]}
              placeholder={`Ask about ${meta.title.toUpperCase()}…`}
              placeholderTextColor={t.ivoryFaint}
              value={question}
              onChangeText={setQuestion}
              multiline
              maxLength={400}
              editable={!chat.sending}
              onSubmitEditing={ask}
            />
            <Pressable
              style={[styles.send, (!question.trim() || chat.sending) && styles.sendDisabled]}
              onPress={ask}
              disabled={!question.trim() || chat.sending}
              hitSlop={10}
            >
              {chat.sending ? (
                <ActivityIndicator size="small" color={t.gold} />
              ) : (
                <Send size={24} color={t.gold} strokeWidth={2.25} />
              )}
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// RN-web draws its own focus ring inside the field, which fights the rim.
const webNoFocusRing = Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : null;

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: c.overlay,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    },
    card: {
      width: '100%',
      maxWidth: 420,
      maxHeight: '85%',
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: radii.panel,
      overflow: 'hidden',
      ...neu.raisedLg,
    },
    // Floating close — no header bar.
    close: {
      position: 'absolute',
      top: 10,
      right: 10,
      width: 28,
      height: 28,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.glass,
      zIndex: 2,
    },
    closeText: { fontSize: 12, fontWeight: '900', color: t.ivorySoft },

    body: { flexGrow: 0 },
    bodyContent: { padding: 18, gap: 8 },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingRight: 32 },
    titleDot: { width: 8, height: 8, borderRadius: 4 },
    title: { fontSize: 16, fontWeight: '900', letterSpacing: 2.5, color: t.ivory, fontFamily: DISPLAY_FONT },
    question: { fontSize: 13, fontWeight: '800', letterSpacing: 0.4, color: t.goldBright },
    lead: { fontSize: 15, fontWeight: '800', color: t.ivory },
    bulletRow: { flexDirection: 'row', gap: 8 },
    bulletDot: { fontSize: 14, lineHeight: 20, fontWeight: '900' },
    bulletText: { flex: 1, fontSize: 13, lineHeight: 20, color: t.ivorySoft },

    subhead: { marginTop: 10, fontSize: 12, fontWeight: '900', letterSpacing: 1, textTransform: 'uppercase' },
    toolRow: { gap: 2, marginTop: 6 },
    toolName: { fontSize: 12, fontWeight: '900', letterSpacing: 0.8 },
    toolText: { fontSize: 13, lineHeight: 19, color: t.ivorySoft },
    greenLabel: { fontWeight: '900', color: t.onPace },
    whyLabel: { fontWeight: '900' },

    answer: {
      marginTop: 14,
      padding: 12,
      borderRadius: radii.tile,
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.lineFaint,
      fontSize: 13,
      lineHeight: 20,
      color: t.ivory,
    },
    answerError: { color: t.lapsed },

    askRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: 10,
      paddingHorizontal: 18,
      paddingTop: 10,
      paddingBottom: 16,
    },
    input: {
      flex: 1,
      minHeight: 40,
      maxHeight: 96,
      borderRadius: radii.tile,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: c.inputFill,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 13,
      color: t.ivory,
      textAlignVertical: 'top',
    },
    send: { paddingBottom: 8 },
    sendDisabled: { opacity: 0.35 },
  }),
);

export default SectionHelpPopup;
